/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext } from 'react';
import ActionIcon from './ActionIcon';
import DonationIcon from '../../../images/icons/Donation.svg';
import DonationBlackIcon from '../../../images/icons/DonationBlack.svg';
import DollarIcon from '../../../images/icons/DollarIcon.svg';
import DollarBlackIcon from '../../../images/icons/DollarBlackIcon.svg';
import PlusIcon from '../../../images/icons/PlusWhite.svg';
import PlusBlackIcon from '../../../images/icons/PlusBlack.svg';
import StarIcon from '../../../images/icons/StarWhite.svg';
import StarBlackIcon from '../../../images/icons/StarBlack.svg';
import ShareIcon from '../../../images/icons/share.svg';
import http from '../../../http-common';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/free-solid-svg-icons";

import SocialShareModal from './SocialShareModal';
import { withShareLang } from '../../../utils/shareUrl';
import EpkCtaButton from './EpkCtaButton';
import WaitlistCtaButton from './WaitlistCtaButton';
import LinkEditModal from './LinkEditModal';
import WaitlistJoinModal from './WaitlistJoinModal';
import WaitlistStatusModal from './WaitlistStatusModal';
import { NotificationContext } from '../../../context/NotificationContext';

export default function EpkSocialAction({ epkInfo, handler, isEditMode, onChange }) {
  const { t, i18n } = useTranslation();
  const user = useSelector((state) => state.user);
  // Redux-persist blacklists `user` (see store.js), so it's rehydrated
  // asynchronously by AuthBootstrap after mount. Wait for that to settle before
  // the waitlist key is scoped by userId, or an anon-scoped check/migration runs
  // first and (for the migration) permanently claims the legacy token before the
  // real id is known.
  const authChecked = useSelector((state) => state.authStatus?.checked);
  
  let userId;
  let userRole;

  if (!user) {
    userId = '0';
    userRole = 'noUser';
  } else {
    userId = user.id;
    userRole = user.role;
  }
  
  const [fepkInfo, setFepkInfo] = useState(epkInfo);
  
  const currentDonateLink = epkInfo?.DonatePayPal_url || epkInfo?.DonateStripe_url || "";
  const hasDonateLinks = Boolean(currentDonateLink);
  // The waitlist CTA preview also counts as "has a CTA" in edit mode, so an owner who
  // has only enabled the waitlist (no custom CTA) still gets the mobile-stack /
  // desktop-inline treatment instead of the plain icon-row-only layout.
  const showWaitlistCta = isEditMode || Boolean(epkInfo?.waitlistConfig?.enabled);
  const hasCta = isEditMode || Boolean(epkInfo?.custom_cta?.url) || Boolean(epkInfo?.waitlistConfig?.enabled);
  // Mobile: when there's a CTA, it stacks full-width above the icon row (unchanged).
  // Desktop: the CTA sits inline with the icon row, centered as one cluster, spaced
  // the same as the icons are from each other (see the shared md:tw-gap-[48px] below).
  const rowPaddingClass = hasCta ? '' : 'sm:tw-px-4 md:tw-px-8';
  const rowJustifyClass = hasCta ? 'md:tw-justify-center' : 'tw-justify-center';
  const iconGroupWidthClass = hasCta ? 'tw-flex-1 md:tw-flex-none' : 'tw-w-full';
  // Mobile stacks the CTA above the icon row (see below), so it sits right under
  // the cover section -- trim the row's top padding on mobile to avoid a big gap there.
  const rowVerticalPaddingClass = hasCta ? 'tw-pt-2 tw-pb-6 md:tw-py-[48px]' : 'tw-py-6 md:tw-py-[48px]';

  const isOwnEpk = String(userId) === String(epkInfo?.film_maker?._id || epkInfo?.film_maker);

  const [usersWishesToDonate, setUsersWishesToDonate] = useState(epkInfo.wishes_to_donate?.length || 0);
  const [usersWishesToBuy, setUsersWishesToBuy] = useState(epkInfo.wishes_to_buy?.length || 0);
  const [usersFavourites, setUsersFavourites] = useState(epkInfo.favourites?.length || 0);
  const [usersLikes, setUsersLikes] = useState(epkInfo.likes?.length || 0);
  const [usersSharings, setUsersSharings] = useState(epkInfo.sharings?.length || 0); 

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const [tempDonateLink, setTempDonateLink] = useState(currentDonateLink);

  const shareUrl = withShareLang(
    window.location.href.startsWith('http') ? window.location.href : `https://${window.location.href}`,
    i18n.language
  );
  const shareTitle = `I've created my new film ${epkInfo.title} #EPK using #KinoKlik free software. Check it out and let me know! #ElectronicPressKit #FilmEPK #NewFilmInProgress`;

  const { incrementNotification, setUserInfo } = useContext(NotificationContext);

  // Waitlist States
  const [waitlistSummary, setWaitlistSummary] = useState({ totalCount: 0, optedInNames: [] });
  const [hasJoinedWaitlist, setHasJoinedWaitlist] = useState(false);
  const [waitlistStatus, setWaitlistStatus] = useState(null);
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

  const waitlistTokenKey = `waitlist_token_${epkInfo._id}_${userId}`;

  const checkWaitlist = async () => {
    // One-time migration off the old, unscoped key (pre-dates per-user scoping) so
    // an existing join isn't silently orphaned by the rename. Only runs when this
    // viewer has no scoped token yet, so it can't override a real one.
    // The anon key is rescued the same way: someone who joined while logged out and
    // then signed in on this browser keeps their entry. The token is *moved*, so the
    // first account to sign in consumes it and a second account on the same device
    // still sees nothing -- the cross-account leak this scoping fixed stays fixed.
    const legacyTokenKeys = [`waitlist_token_${epkInfo._id}`];
    if (String(userId) !== '0') legacyTokenKeys.push(`waitlist_token_${epkInfo._id}_0`);

    if (!localStorage.getItem(waitlistTokenKey)) {
      for (const legacyTokenKey of legacyTokenKeys) {
        const legacyToken = localStorage.getItem(legacyTokenKey);
        if (!legacyToken) continue;
        localStorage.setItem(waitlistTokenKey, legacyToken);
        localStorage.removeItem(legacyTokenKey);
        break;
      }
    }

    const waitlistToken = localStorage.getItem(waitlistTokenKey);
    const userToken = user?.token;

    // A logged-in user's own session always wins over a leftover anon token
    // scoped to this device, so switching accounts on the same browser can't
    // surface a previous account's waitlist entry.
    const tokenToUse = userToken || waitlistToken;

    if (!tokenToUse) {
      setHasJoinedWaitlist(false);
      setWaitlistStatus(null);
      return;
    }

    try {
      const response = await http.get(`/waitlist/${epkInfo._id}/me`, {
        headers: { Authorization: `Bearer ${tokenToUse}` }
      });
      if (response.data?.entry) {
        setHasJoinedWaitlist(true);
        setWaitlistStatus(response.data);
      } else {
        setHasJoinedWaitlist(false);
        setWaitlistStatus(null);
      }
    } catch (err) {
      setHasJoinedWaitlist(false);
      setWaitlistStatus(null);
      if (waitlistToken && err.response?.status === 401) {
        localStorage.removeItem(waitlistTokenKey);
      }
    }
  };

  const loadWaitlistSummary = async () => {
    try {
      const response = await http.get(`/waitlist/${epkInfo._id}/summary`);
      setWaitlistSummary(response.data || { totalCount: 0, optedInNames: [] });
    } catch (err) {
      console.error(err);
    }
  };

  // The "N joined" badge is public and unauthenticated, so it must not sit behind
  // the auth round-trip -- anonymous visitors would otherwise wait on a check that
  // never concerns them.
  useEffect(() => {
    if (epkInfo?._id && epkInfo?.waitlistConfig?.enabled) {
      loadWaitlistSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epkInfo?._id, epkInfo?.waitlistConfig?.enabled]);

  useEffect(() => {
    if (epkInfo?._id && epkInfo?.waitlistConfig?.enabled && authChecked) {
      checkWaitlist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epkInfo?._id, epkInfo?.waitlistConfig?.enabled, user?.token, authChecked]);

  const handleJoinSuccess = (joinData) => {
    setIsJoinModalOpen(false);
    setIsStatusModalOpen(true);
    checkWaitlist();
    loadWaitlistSummary();
  };

  const handleLeaveSuccess = () => {
    setIsStatusModalOpen(false);
    setHasJoinedWaitlist(false);
    setWaitlistStatus(null);
    loadWaitlistSummary();
  };

  useEffect(() => {
    setTempDonateLink(currentDonateLink);
  }, [currentDonateLink]);

  useEffect(() => {
    try {
      if (!isEditMode && epkInfo._id) {
        http.get(`/fepks/${epkInfo._id}`).then((response) => {
          setFepkInfo(response.data);
          setUsersWishesToDonate(response.data.wishes_to_donate?.length || 0);
          setUsersWishesToBuy(response.data.wishes_to_buy?.length || 0);
          setUsersFavourites(response.data.favourites?.length || 0);
          setUsersLikes(response.data.likes?.length || 0);
          setUsersSharings(response.data.sharings?.length || 0); 
        });
      }
    } catch (error) {
      console.log(error);
    }
  }, [epkInfo._id, isEditMode]);

  const checkActive = (arr) => arr?.some((item) => item === userId || item?._id === userId);

  const actionList = [
    {
      name: 'wish_to_donate',
      icon: (hasDonateLinks || isEditMode)
        ? (checkActive(fepkInfo?.wishes_to_donate) && !isEditMode)
          ? DonationBlackIcon
          : DonationIcon
        : null, 
      number: hasDonateLinks ? usersWishesToDonate : 0,
      hover: isEditMode ? t('Add Donation Link') : t('Donate'),
      isActive: checkActive(fepkInfo?.wishes_to_donate),
    },
    {
      name: 'wish_to_buy',
      icon: checkActive(fepkInfo?.wishes_to_buy) ? DollarBlackIcon : DollarIcon,
      number: usersWishesToBuy,
      hover: t('Buy'),
      isActive: checkActive(fepkInfo?.wishes_to_buy),
    },
    {
      name: 'favorites',
      icon: checkActive(fepkInfo?.favourites) ? PlusBlackIcon : PlusIcon,
      number: usersFavourites,
      hover: t('Add To Favorites'),
      isActive: checkActive(fepkInfo?.favourites),
    },
    {
      name: 'likes',
      icon: checkActive(fepkInfo?.likes) ? StarBlackIcon : StarIcon,
      number: usersLikes,
      hover: t('Like'),
      isActive: checkActive(fepkInfo?.likes),
    },
    {
      name: 'share',
      icon: ShareIcon,
      number: usersSharings,
      hover: t('Share'),
      isActive: checkActive(fepkInfo?.sharings),
    },
  ];

  const handlers = {
    clickHandler: (name) => {
      if (isEditMode) {
        if (name === 'wish_to_donate') {
          setIsDonateModalOpen(true);
        }
        return; 
      }

      if (isOwnEpk && name !== 'share' && name !== 'waitlist') {
        return; // Prevent interacting with own EPK (except sharing and waitlist)
      }

      if (name === 'waitlist') {
        if (hasJoinedWaitlist) {
          setIsStatusModalOpen(true);
        } else {
          setIsJoinModalOpen(true);
        }
        return;
      }

      if (name === 'share') {
        setIsShareModalOpen(prev => !prev);
        if (user && userId !== '0') {
          http.post(`/fepks/sharing/${epkInfo._id}/${userId}`)
            .then((response) => {
              setFepkInfo(response.data);
              setUsersSharings(response.data.sharings?.length || 0);
            })
            .catch((error) => console.error(error));
        }
      } else if (!user) {
        handler(); 
      } else {
        let incrementValue = 0;
        switch (name) {
          case 'wish_to_donate':
            handler('wish_to_donate'); 
            http.post(`/fepks/wishestodonate/${epkInfo._id}/${userId}`)
              .then((response) => {
                setFepkInfo(response.data);
                setUsersWishesToDonate(response.data.wishes_to_donate.length);
              })
              .catch((error) => console.error(error));
            break;

          case 'wish_to_buy':
            incrementValue = checkActive(fepkInfo?.wishes_to_buy) ? 0 : 1;
            http.post(`/fepks/wishestobuy/${epkInfo._id}/${userId}`)
              .then((response) => {
                setFepkInfo(response.data);
                setUserInfo(response.data.film_maker);
                setUsersWishesToBuy(response.data.wishes_to_buy.length);
                incrementNotification(incrementValue);
              })
              .catch((error) => console.error(error));
            break;

          case 'favorites':
            incrementValue = checkActive(fepkInfo?.favourites) ? 0 : 1;
            http.post(`/fepks/favourite/${epkInfo._id}/${userId}`)
              .then((response) => {
                setFepkInfo(response.data);
                setUserInfo(response.data.film_maker);
                setUsersFavourites(response.data.favourites.length);
                incrementNotification(incrementValue);
              })
              .catch((error) => console.error(error));
            break;

          case 'likes':
            incrementValue = checkActive(fepkInfo?.likes) ? 0 : 1;
            http.post(`/fepks/like/${epkInfo._id}/${userId}`)
              .then((response) => {
                setFepkInfo(response.data);
                setUserInfo(response.data.film_maker);
                setUsersLikes(response.data.likes.length);
                incrementNotification(incrementValue);
              })
              .catch((error) => console.error(error));
            break;
            
          default:
            break;
        }
      }
    }
  };

  const handleSaveDonateLink = () => {
    onChange("DonatePayPal_url", tempDonateLink);
    setIsDonateModalOpen(false);
  };

  return (
    <div className={`tw-relative tw-flex tw-w-full tw-items-center tw-justify-center tw-bg-opacity-100 ${rowVerticalPaddingClass}`}>
      
      {/* FIX: Set px-0 and justify-between for mobile to evenly space out the icons across the full width */}
      <div className={`tw-flex ${hasCta ? 'tw-flex-col md:tw-flex-row' : 'tw-flex-row'} tw-items-center tw-w-full tw-max-w-[1280px] tw-px-0 ${rowPaddingClass} tw-gap-4 md:tw-gap-[48px] ${rowJustifyClass}`}>

        {hasCta && (
          // On mobile both pills stack, centered at their natural width (same as the
          // custom CTA has always rendered alone). On desktop both sit inline,
          // side by side, centered as one cluster with the icon row. Either pill can
          // independently render nothing (EpkCtaButton and WaitlistCtaButton both
          // self-guard), so this works whether one or both are actually present.
          <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-center tw-justify-center tw-gap-3 tw-w-full md:tw-w-auto md:tw-shrink-0">
            {showWaitlistCta && (
              <WaitlistCtaButton
                epkInfo={epkInfo}
                isEditMode={isEditMode}
                hasJoinedWaitlist={hasJoinedWaitlist}
                totalCount={waitlistSummary.totalCount}
                onClick={() => handlers.clickHandler('waitlist')}
              />
            )}
            <EpkCtaButton epkInfo={epkInfo} isEditMode={isEditMode} onChange={onChange} />
          </div>
        )}

        <div className={`tw-flex tw-flex-row tw-items-center tw-justify-between sm:tw-justify-center ${iconGroupWidthClass} tw-gap-1 min-[375px]:tw-gap-1.5 min-[400px]:tw-gap-2 sm:tw-gap-6 md:tw-gap-6 lg:tw-gap-[48px]`}>

          {actionList.map((action, index) => {
            if (action.icon === null) return null;

            const isInactiveInEditMode = isEditMode && action.name !== 'wish_to_donate';
            const isInactiveForOwner = isOwnEpk && !isEditMode && action.name !== 'share' && action.name !== 'waitlist';

            return (
              <div
                key={index}
                className={`tw-relative tw-flex tw-justify-center tw-transition-all ${
                  isInactiveInEditMode || isInactiveForOwner ? 'tw-opacity-40 tw-grayscale tw-pointer-events-none' : ''
                }`}
              >
                {action.name === 'share' && !isEditMode && (
                  <SocialShareModal
                    isOpen={isShareModalOpen}
                    shareUrl={shareUrl}
                    shareTitle={shareTitle}
                    epkTitle={epkInfo.title}
                    closeModal={() => setIsShareModalOpen(false)}
                  />
                )}

                <ActionIcon
                  name={action.name}
                  icon={action.icon}
                  number={action.number}
                  handlers={handlers}
                  title={action.hover}
                  isActive={isEditMode ? false : action.isActive}
                />
              </div>
            );
          })}
        </div>
      </div>

      {isDonateModalOpen && (
        <LinkEditModal
          title={t("Please input your donation link")}
          onCancel={() => {
            setTempDonateLink(currentDonateLink);
            setIsDonateModalOpen(false);
          }}
          onSave={handleSaveDonateLink}
        >
          <div className="tw-relative tw-w-full">
            <div className="tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2">
              <FontAwesomeIcon icon={faLink} className="tw-text-[#AA8894]" />
            </div>
            <input
              type="url"
              value={tempDonateLink}
              onChange={(e) => setTempDonateLink(e.target.value)}
              placeholder={t("Enter URL (e.g., PayPal, Stripe)...")}
              className="tw-w-full tw-bg-[#280D41] tw-border-2 tw-border-dashed tw-border-[#FF43A7]/40 tw-rounded-lg tw-py-[16px] tw-pl-[48px] tw-pr-4 tw-text-[16px] tw-text-white tw-outline-none placeholder:tw-text-[#F0DBFF]/30 tw-transition-colors focus:tw-border-[#FF43A7]"
            />
          </div>
        </LinkEditModal>
      )}

      {isJoinModalOpen && (
        <WaitlistJoinModal
          fepkId={epkInfo._id}
          storageKey={waitlistTokenKey}
          onClose={() => setIsJoinModalOpen(false)}
          onSuccess={handleJoinSuccess}
        />
      )}

      {isStatusModalOpen && (
        <WaitlistStatusModal
          fepkId={epkInfo._id}
          storageKey={waitlistTokenKey}
          sessionToken={user?.token}
          onClose={() => setIsStatusModalOpen(false)}
          onLeave={handleLeaveSuccess}
        />
      )}

    </div>
  );
}